import { useEffect, useState } from "react";
import { api, setToken, getToken, clearToken } from "rsa/api";
import MapView from "rsa/MapView";
import RequestTracker from "./RequestTracker";
import "rsa/rsa.scss";

const DEFAULT_LOC = { lat: 19.076, lng: 72.8777 };

export default function CustomerRsa() {
  const [token, setTok] = useState(getToken("customer"));
  const [step, setStep] = useState(1);         // 1 vehicle, 2 details, 3 tracking
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // login
  const [email, setEmail] = useState("prasad1187@gmail.com");
  const [password, setPassword] = useState("prasad@123");

  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(null);
  const [types, setTypes] = useState([]);
  const [serviceTypeId, setServiceTypeId] = useState(null);
  const [loc, setLoc] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [severity, setSeverity] = useState("high");
  const [ambulance, setAmbulance] = useState(false);
  const [fire, setFire] = useState(false);
  const [police, setPolice] = useState(false);
  const [requestId, setRequestId] = useState(null);

  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  const login = () => run(async () => {
    const r = await api("POST", "/auth/login", { body: { email, password } });
    if (!r.user.customerId) throw new Error("This account is not a customer.");
    setToken("customer", r.token); setTok(r.token);
  });
  const logout = () => { clearToken("customer"); setTok(null); setStep(1); setRequestId(null); };

  useEffect(() => {
    if (!token) return;
    api("GET", "/rsa/service-types").then(setTypes).catch(() => {});
    api("GET", "/customers/me/vehicles", { token })
      .then((v) => { setVehicles(v); if (v[0]) setVehicleId(v[0].id); })
      .catch((e) => { if (e.status === 401) logout(); });
  }, [token]); // eslint-disable-line

  const getLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(DEFAULT_LOC);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(DEFAULT_LOC), { enableHighAccuracy: true, timeout: 8000 });
  });

  const goDetails = () => run(async () => {
    const l = await getLocation(); setLoc(l);
    const near = await api("GET", `/rsa/vendors/nearby?lat=${l.lat}&lng=${l.lng}`, { token });
    setVendors(near);
    setStep(2);
  });

  // recompute estimate whenever reason changes (uses nearest vendor)
  useEffect(() => {
    if (!serviceTypeId || !loc || vendors.length === 0) { setEstimate(null); return; }
    api("POST", "/rsa/estimates", { token, body: { serviceTypeId, lat: loc.lat, lng: loc.lng, vendorId: vendors[0].id } })
      .then(setEstimate).catch(() => setEstimate(null));
  }, [serviceTypeId]); // eslint-disable-line

  const submit = () => run(async () => {
    const st = types.find((t) => t.id === serviceTypeId);
    const r = await api("POST", "/rsa/requests", { token, body: {
      serviceTypeId, vehicleId, lat: loc.lat, lng: loc.lng, severity,
      ambulanceRequested: ambulance || (st?.is_ambulance_eligible && severity === "critical"),
      fireRequested: fire, policeRequested: police,
      description: `${st?.name} — assistance requested`,
    }});
    setRequestId(r.request.id); setStep(3);
  });

  if (!token)
    return (
      <div className="avah-rsa">
        <div className="avah-rsa__top"><h1>🚗 Avahan Roadside Assistance</h1><p>Sign in to request help</p></div>
        <div className="avah-rsa__shell">
          <div className="avah-rsa__card">
            <label className="avah-rsa__label">Email</label>
            <input className="avah-rsa__input" value={email} onChange={(e) => setEmail(e.target.value)} />
            <label className="avah-rsa__label" style={{ marginTop: 12 }}>Password</label>
            <input className="avah-rsa__input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 16 }} disabled={busy} onClick={login}>
              {busy ? "Signing in…" : "Sign in"}</button>
            {err && <div className="avah-rsa__err">{err}</div>}
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 0 }}>Demo: prasad1187@gmail.com / prasad@123</p>
          </div>
        </div>
      </div>
    );

  const selVehicle = vehicles.find((v) => v.id === vehicleId);
  return (
    <div className="avah-rsa">
      <div className="avah-rsa__top">
        <h1>🚗 Roadside Assistance</h1>
        <p>Emergency &amp; breakdown help near you</p>
      </div>
      <div className="avah-rsa__shell">
        {step === 1 && (
          <div className="avah-rsa__card">
            <label className="avah-rsa__label">Which vehicle needs help?</label>
            {vehicles.length === 0 && <p style={{ color: "#6b7280", fontSize: 14 }}>No vehicles on your account.</p>}
            {vehicles.map((v) => (
              <button key={v.id} type="button"
                className={`avah-rsa__reason${vehicleId === v.id ? " avah-rsa__reason--on" : ""}`}
                style={{ width: "100%", marginBottom: 8 }} onClick={() => setVehicleId(v.id)}>
                <b>{v.brand} {v.model} · {v.registration_number}</b>
                <span>{v.fuel} · {v.vehicle_use}{v.has_insurance ? " · insured 🛡️" : ""}</span>
              </button>
            ))}
            <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 8 }}
              disabled={busy || !vehicleId} onClick={goDetails}>{busy ? "Finding help…" : "Continue"}</button>
            {err && <div className="avah-rsa__err">{err}</div>}
            <button className="avah-rsa__btn avah-rsa__btn--ghost" style={{ marginTop: 8 }} onClick={logout}>Sign out</button>
          </div>
        )}

        {step === 2 && (
          <>
            <div className="avah-rsa__card" style={{ padding: 12 }}>
              <div className="avah-rsa__label" style={{ padding: "0 4px 8px" }}>
                {vendors.length} collision-care partners near you
              </div>
              <MapView pickup={loc} vendors={vendors} height={220} />
            </div>
            <div className="avah-rsa__card">
              <label className="avah-rsa__label">What do you need help with?</label>
              <div className="avah-rsa__reasons">
                {types.map((t) => (
                  <button key={t.id} type="button"
                    className={`avah-rsa__reason${serviceTypeId === t.id ? " avah-rsa__reason--on" : ""}`}
                    onClick={() => { setServiceTypeId(t.id); if (t.is_ambulance_eligible) setSeverity("critical"); }}>
                    <b>{t.name}</b><span>from ₹{Math.round(t.base_fee)}</span>
                  </button>
                ))}
              </div>
              {estimate && (
                <div className="avah-rsa__vehicle" style={{ marginTop: 14 }}>
                  Estimated fee for <b>{selVehicle?.brand} {selVehicle?.model}</b>:
                  <div style={{ fontSize: 22, fontWeight: 700, color: "var(--avah-ink)" }}>
                    ₹{estimate.estimatedFee}
                  </div>
                  <span style={{ fontSize: 12 }}>₹{estimate.baseFee} base + ₹{estimate.perKmFee}/km × {estimate.distanceKm} km
                    {estimate.etaMinutes ? ` · ~${estimate.etaMinutes} min away` : ""}</span>
                </div>
              )}
              <label className="avah-rsa__label" style={{ marginTop: 16 }}>Severity</label>
              <div className="avah-rsa__row">
                {["low", "medium", "high", "critical"].map((s) => (
                  <button key={s} type="button" onClick={() => setSeverity(s)} style={{ cursor: "pointer", border: "none" }}
                    className={`avah-rsa__pill${severity === s ? " avah-rsa__pill--live" : ""}`}>{s}</button>
                ))}
              </div>
              <label style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={ambulance} onChange={(e) => setAmbulance(e.target.checked)} />
                🚑 Also send an ambulance (severe injury)
              </label>
              <label style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={fire} onChange={(e) => setFire(e.target.checked)} />
                🚒 Alert fire brigade (fire / smoke / fuel leak)
              </label>
              <label style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={police} onChange={(e) => setPolice(e.target.checked)} />
                👮 Alert police (accident / dispute / safety)
              </label>
            </div>
            <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 14 }}
              disabled={busy || !serviceTypeId} onClick={submit}>
              {busy ? "Requesting…" : "Request assistance"}</button>
            {err && <div className="avah-rsa__err">{err}</div>}
          </>
        )}

        {step === 3 && requestId && <RequestTracker requestId={requestId} token={token} pickup={loc} />}
      </div>
    </div>
  );
}
